import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCustomFields,
  useDeleteCustomField,
  useSaveCustomField,
  type CustomField,
  type CustomFieldEntity,
  type CustomFieldValue,
} from '@/api/customFields';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { CustomFieldInputs } from '@/components/CustomFieldInputs';
import { useApiError } from '@/hooks/useApiError';

const TYPES = ['TEXT', 'NUMBER', 'LINK', 'SELECT', 'MULTI_SELECT', 'CHECKBOX'] as const;

/** Benutzerdefinierte Felder (CF-01/02) und Profilfelder (TEAM-06). */
export function CustomFieldsSettings() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const [entity, setEntity] = useState<CustomFieldEntity>('TIME_ENTRY');
  const { data: fields = [] } = useCustomFields(ctx.workspaceId);
  const remove = useDeleteCustomField(ctx.workspaceId);
  const onError = useApiError();
  const [editing, setEditing] = useState<CustomField | 'new' | null>(null);
  const available = entity === 'USER' ? ctx.hasFeature('USER_FIELDS') : ctx.hasFeature('CUSTOM_FIELDS');
  const list = fields.filter((f) => f.entity === entity);

  return (
    <Stack maw={1000}>
      <Group justify="space-between">
        <SegmentedControl
          value={entity}
          onChange={(v) => setEntity(v as CustomFieldEntity)}
          data={[
            { value: 'TIME_ENTRY', label: t('customFields.entityTimeEntry') },
            { value: 'USER', label: t('customFields.entityUser') },
          ]}
        />
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')} disabled={!available}>
          {t('customFields.add')}
        </Button>
      </Group>
      {!available && <Alert color="gray">{t('settings.notInPlan')}</Alert>}
      <Text size="sm" c="dimmed">
        {entity === 'USER' ? t('customFields.userHint') : t('customFields.entryHint')}
      </Text>
      <Paper withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('customFields.name')}</Table.Th>
              <Table.Th>{t('customFields.type')}</Table.Th>
              <Table.Th>{t('customFields.properties')}</Table.Th>
              <Table.Th w={90} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" size="sm">
                    {t('customFields.none')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {list.map((f) => (
              <Table.Tr key={f.id} opacity={f.archived ? 0.5 : 1}>
                <Table.Td>{f.name}</Table.Td>
                <Table.Td>
                  {t(`customFields.types.${f.type}`)}
                  {f.options.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {f.options.join(', ')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {f.required && <Badge size="sm">{t('customFields.required')}</Badge>}
                    {f.adminOnly && <Badge size="sm" color="orange">{t('customFields.adminOnly')}</Badge>}
                    {f.visibility === 'ADMINS' && <Badge size="sm" color="gray">{t('customFields.hidden')}</Badge>}
                    {entity === 'TIME_ENTRY' && !f.allProjects && <Badge size="sm" color="cyan">{t('customFields.selectedProjects')}</Badge>}
                    {f.archived && <Badge size="sm" color="gray">{t('common.archived')}</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end">
                    <ActionIcon variant="subtle" onClick={() => setEditing(f)} aria-label={t('common.edit')}>
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('common.delete')}
                      onClick={() =>
                        modals.openConfirmModal({
                          title: t('customFields.deleteTitle'),
                          children: <Text size="sm">{t('customFields.deleteText', { name: f.name })}</Text>,
                          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
                          confirmProps: { color: 'red' },
                          onConfirm: () => remove.mutate(f.id, { onError }),
                        })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      {editing && <FieldModal field={editing === 'new' ? null : editing} entity={entity} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

function FieldModal({ field, entity, onClose }: { field: CustomField | null; entity: CustomFieldEntity; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const save = useSaveCustomField(ctx.workspaceId);
  const onError = useApiError();
  const [name, setName] = useState(field?.name ?? '');
  const [type, setType] = useState<CustomField['type']>(field?.type ?? 'TEXT');
  const [options, setOptions] = useState<string[]>(field?.options ?? []);
  const [required, setRequired] = useState(field?.required ?? false);
  const [adminOnly, setAdminOnly] = useState(field?.adminOnly ?? false);
  const [hidden, setHidden] = useState(field?.visibility === 'ADMINS');
  const [allProjects, setAllProjects] = useState(field?.allProjects ?? true);
  const [archived, setArchived] = useState(field?.archived ?? false);
  const [defaultValue, setDefaultValue] = useState<CustomFieldValue>((field?.defaultValue as CustomFieldValue) ?? null);
  const choice = type === 'SELECT' || type === 'MULTI_SELECT';
  const preview: CustomField = {
    id: 'default',
    entity,
    name: t('customFields.defaultValue'),
    type,
    options,
    required: false,
    adminOnly: false,
    visibility: 'EVERYONE',
    allProjects: true,
    position: 0,
    archived: false,
  };

  return (
    <Modal opened onClose={onClose} title={field ? t('customFields.edit') : t('customFields.add')}>
      <Stack>
        <TextInput label={t('customFields.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} required data-autofocus />
        <Select
          label={t('customFields.type')}
          data={TYPES.map((v) => ({ value: v, label: t(`customFields.types.${v}`) }))}
          value={type}
          onChange={(v) => {
            setType((v as CustomField['type']) ?? 'TEXT');
            setDefaultValue(null);
          }}
          allowDeselect={false}
        />
        {choice && (
          <TagsInput label={t('customFields.options')} description={t('customFields.optionsHint')} value={options} onChange={setOptions} clearable />
        )}
        {entity === 'TIME_ENTRY' && (
          <CustomFieldInputs
            fields={[{ field: preview }]}
            values={{ default: defaultValue }}
            onChange={(v) => setDefaultValue(v.default ?? null)}
            isAdmin
          />
        )}
        <Switch label={t('customFields.required')} checked={required} onChange={(e) => setRequired(e.currentTarget.checked)} />
        <Switch
          label={t('customFields.adminOnly')}
          description={t('customFields.adminOnlyHint')}
          checked={adminOnly}
          onChange={(e) => setAdminOnly(e.currentTarget.checked)}
        />
        <Switch
          label={t('customFields.hidden')}
          description={t('customFields.hiddenHint')}
          checked={hidden}
          onChange={(e) => setHidden(e.currentTarget.checked)}
        />
        {entity === 'TIME_ENTRY' && (
          <Switch
            label={t('customFields.allProjects')}
            description={t('customFields.allProjectsHint')}
            checked={allProjects}
            onChange={(e) => setAllProjects(e.currentTarget.checked)}
          />
        )}
        {field && <Switch label={t('common.archived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={save.isPending}
            disabled={!name.trim() || (choice && options.length === 0)}
            onClick={() =>
              save.mutate(
                {
                  id: field?.id,
                  name,
                  entity,
                  type,
                  options: choice ? options : [],
                  required,
                  adminOnly,
                  visibility: hidden ? 'ADMINS' : 'EVERYONE',
                  allProjects,
                  archived,
                  defaultValue: (defaultValue ?? null) as never,
                },
                { onSuccess: onClose, onError },
              )
            }
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
